import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    payload: String,
  }

  connect() {
    this.payload = this.parsePayload(this.payloadValue)
    if (!this.payload) return
    if (this.payload.transposed) this._applyTranspose()
    this.renderChart()
    const cb = this.element.querySelector("[data-transpose-checkbox]")
    if (cb) cb.checked = !!this.payload.transposed
  }

  disconnect() {
    const chartContainer = this.element.querySelector("[data-chart]")
    if (chartContainer) window.echarts?.getInstanceByDom(chartContainer)?.dispose()
  }

  swapAxes() {
    if (!this.payload || this.payload.type !== "bar") return
    this.payload = { ...this.payload, horizontal: !this.payload.horizontal }
    const chartContainer = this.element.querySelector("[data-chart]")
    if (!chartContainer) return
    const chart = window.echarts.getInstanceByDom(chartContainer)
    if (!chart) return
    chart.setOption(this.buildOption("bar", this.payload), true)
  }

  transposeData() {
    if (!this.payload || this.payload.type !== "bar") return
    this._applyTranspose()
    this.renderChart()
  }

  _applyTranspose() {
    const { labels, series } = this.payload
    if (!labels || !series || series.length === 0) return
    const newLabels = series.map((s) => s.name)
    const newSeries = labels.map((label, i) => ({
      name: label,
      data: series.map((s) => (s.data || [])[i] ?? 0),
    }))
    this.payload = { ...this.payload, labels: newLabels, series: newSeries }
  }

  parsePayload(raw) {
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch (_e) {
      return null
    }
  }

  renderChart() {
    if (!window.echarts) return
    if (!this.payload) return
    const type = this.payload.type
    if (!["pie", "donut", "bar", "line", "bubble", "scatter"].includes(type)) return

    const chartContainer = this.element.querySelector("[data-chart]")
    if (!chartContainer) return

    // Always dispose any existing instance so stale options (e.g. stack) never bleed through
    window.echarts.getInstanceByDom(chartContainer)?.dispose()
    const chart = window.echarts.init(chartContainer)
    chart.setOption(this.buildOption(type, this.payload))
  }

  cssVar(name, fallback) {
    if (typeof window === "undefined") return fallback
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim()
    return value || fallback
  }

  formatValue(value) {
    const format = this.payload?.format || {}
    if (value === null || value === undefined) return "-"
    const numeric = typeof value === "number" ? value : Number(value)
    if (Number.isNaN(numeric)) return value
    if (format.show_percent) {
      const decimals = Number.isInteger(format.percent_decimals) ? format.percent_decimals : 1
      return `${numeric.toFixed(decimals)}%`
    }
    const decimals = Number.isInteger(format.value_decimals) ? format.value_decimals : 0
    return numeric.toFixed(decimals)
  }

  buildOption(type, payload) {
    const textColor = this.cssVar("--brand-text", "#3a4d55")
    const mutedColor = this.cssVar("--brand-text-muted", "#6b777e")
    const axisLabelStyle = { color: textColor }
    const legendStyle = { type: "scroll", textStyle: { color: textColor } }
    const showPercent = payload?.format?.show_percent
    if (type === "pie" || type === "donut") {
      const total = (payload.series || []).reduce((sum, item) => sum + (item.value || 0), 0)
      const percentDecimals = Number.isInteger(payload?.format?.percent_decimals) ? payload.format.percent_decimals : 1
      const useImageLabels = !!payload?.format?.label_images
      const seriesData = (payload.series || []).map((item) => {
        const hasImage = useImageLabels && !!item?.image
        return {
          ...item,
          label: hasImage
            ? { show: true, formatter: "{logo|}", rich: { logo: { height: 32, width: 32, backgroundColor: { image: item.image } } } }
            : { show: true, color: textColor, formatter: () => {
                if (!showPercent || total === 0) return item.name
                const pct = (item.value / total) * 100
                return `${item.name} (${pct.toFixed(percentDecimals)}%)`
              }
            },
          labelLine: { show: true, lineStyle: { color: textColor } },
        }
      })
      const centerY = useImageLabels ? "64%" : "62%"
      const radius  = type === "donut" ? ["35%", "58%"] : "58%"
      const pieLegend = useImageLabels
        ? { ...legendStyle, padding: [5, 5, 60, 5] }
        : legendStyle
      return {
        legend: pieLegend,
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            if (!showPercent || total === 0) return `${params.name}: ${this.formatValue(params.value)}`
            const pct = (params.value / total) * 100
            return `${params.name}: ${this.formatValue(params.value)} (${pct.toFixed(percentDecimals)}%)`
          },
        },
        series: [
          {
            type: "pie",
            radius,
            center: ["50%", centerY],
            data: seriesData,
          },
        ],
      }
    }

    if (type === "bar") {
      const horizontal  = !!payload.horizontal
      const stacked     = !!payload.stacked
      const rawLabels   = payload.labels || []
      const rawImages   = payload.label_images || []

      // Reverse both in sync for horizontal so highest value sits at top.
      const shouldReverse = horizontal
      const labels = shouldReverse ? rawLabels.slice().reverse() : rawLabels
      const images = shouldReverse ? rawImages.slice().reverse() : rawImages
      const hasImages = images.some(Boolean)

      // Build ECharts rich map — one keyed entry per slot that has an image
      const richMap = {}
      if (hasImages) {
        images.forEach((img, idx) => {
          if (img) richMap[`i${idx}`] = { height: 24, width: 56, backgroundColor: { image: img } }
        })
      }

      const categoryAxis = {
        type: "category",
        data: labels,
        axisLabel: hasImages ? {
          ...axisLabelStyle,
          formatter: (_, idx) => images[idx] ? `{i${idx}|}` : (labels[idx] || ""),
          rich: richMap,
          height: 28,
          interval: 0,
        } : horizontal
          ? { ...axisLabelStyle, width: 120, overflow: "truncate" }
          : { ...axisLabelStyle, rotate: 30, overflow: "truncate", width: 80 },
      }
      const valueAxis = { type: "value", axisLabel: axisLabelStyle }

      return {
        tooltip: { trigger: "axis", valueFormatter: (value) => this.formatValue(value) },
        grid: horizontal
          ? { left: hasImages ? "5%" : "30%", right: "8%", containLabel: hasImages }
          : {},
        xAxis: horizontal ? valueAxis : categoryAxis,
        yAxis: horizontal ? categoryAxis : valueAxis,
        legend: legendStyle,
        series: (payload.series || []).map((series) => ({
          name: series.name,
          type: "bar",
          ...(stacked ? { stack: "total" } : {}),
          label: { color: textColor },
          data: shouldReverse ? (series.data || []).slice().reverse() : (series.data || []),
        })),
        textStyle: { color: textColor },
      }
    }

    if (type === "scatter") {
      // 2D scatter — x-axis is `xLabels` (e.g. brands), y-axis is `yLabels`
      // (e.g. models), each point is { value: [xIdx, yIdx, count], name }.
      // Point size scales with count.
      const xLabels = payload.xLabels || payload.labels || []
      const yLabels = payload.yLabels || []
      const points  = payload.points || []
      const maxVal  = points.reduce((m, p) => Math.max(m, p.value?.[2] || 0), 0) || 1
      return {
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            const x = xLabels[params.value[0]] || ""
            const y = yLabels[params.value[1]] || ""
            return `${x} · ${y}: ${this.formatValue(params.value[2])}`
          },
        },
        grid: { top: "8%", bottom: "18%", left: "12%", right: "4%", containLabel: true },
        xAxis: { type: "category", data: xLabels, axisLabel: { color: textColor, fontSize: 10, rotate: xLabels.length > 4 ? 30 : 0 } },
        yAxis: { type: "category", data: yLabels, axisLabel: { color: textColor, fontSize: 10 } },
        series: [{
          type: "scatter",
          data: points,
          symbolSize: (val) => Math.max(8, Math.round((val[2] / maxVal) * 40)),
          itemStyle: { opacity: 0.85 },
        }],
        textStyle: { color: textColor },
      }
    }

    if (type === "bubble") {
      const data    = payload.data || []
      const labels  = payload.labels || []
      const maxVal  = data.length > 0 ? Math.max(...data) : 1
      const n       = data.length
      const scatterData = data.map((val, i) => ({ name: labels[i] || "", value: [i, 0, val] }))
      return {
        tooltip: {
          trigger: "item",
          formatter: (params) => `${params.name}: ${this.formatValue(params.value[2])}`,
        },
        grid: { top: "8%", bottom: "26%", left: "4%", right: "4%", containLabel: false },
        xAxis: { show: false, min: -0.5, max: Math.max(n - 0.5, 0.5) },
        yAxis: { show: false, min: -1, max: 1 },
        series: [{
          type: "scatter",
          data: scatterData,
          symbolSize: (val) => maxVal > 0 ? Math.max(16, Math.round((val[2] / maxVal) * 80)) : 16,
          label: {
            show: true,
            position: "bottom",
            formatter: (params) => `${params.name}\n${this.formatValue(params.value[2])}`,
            color: textColor,
            fontSize: 10,
            overflow: "truncate",
            width: 72,
          },
          labelLayout: { hideOverlap: true },
          itemStyle: { opacity: 0.85 },
        }],
        textStyle: { color: textColor },
      }
    }

    return {
      legend: legendStyle,
      tooltip: {
        trigger: "axis",
        valueFormatter: (value) => this.formatValue(value),
      },
      xAxis: { type: "category", data: payload.labels || [], axisLabel: axisLabelStyle },
      yAxis: { type: "value", axisLabel: axisLabelStyle },
      series: (payload.series || []).map((series) => ({
        name: series.name,
        type: "line",
        label: { color: textColor },
        data: series.data || [],
      })),
      textStyle: { color: textColor },
    }
  }
}
